import cartDuck from 'components/cart/duck'
import modalDuck from 'components/modal/duck'
import StateStorage from 'lib/state-storage'
import { rememberPreferredSize } from 'lib/size-preference'
import Tracking from 'lib/tracking'
import ExchangeRateApiHandler from 'lib/api/exchange'
import { segmentTrackProductAdded } from 'lib/segment'
import { updateLagacyShoppingTrack } from 'lib/auth'
import segmentQuickAddToBagClicked from 'components/shared/quick-add-to-bag-modal/quick-add-to-bag-segment/quickAddToBagSegment'
import { getCartQuantity } from 'components/shared/product/quick-add/request'

export const ADD_TO_BAG_API_CODE = {
  overLimit: 'QUANTITY_OVER_LIMIT',
  soldOut: 'ITEM_SOLD_OUT',
}

export function getAddedProdSizeQtyInCart(cart, product, sizeId) {
  const listProductMatchId = cart.items.filter(
    ({ id_product }) => String(id_product) === String(product.id_product),
  )
  const productInCart = listProductMatchId.find(({ attributes }) =>
    attributes?.find(
      ({ selected, id_product_attribute }) =>
        selected && String(id_product_attribute) === String(sizeId),
    ),
  )

  const selectedAttribute = productInCart?.attributes?.find(
    ({ id_product_attribute }) =>
      String(id_product_attribute) === String(sizeId),
  )

  if (!productInCart) return 1
  const cartQuantity = Number(productInCart.quantity)
  const limits = [
    selectedAttribute?.stock?.quantity,
    productInCart.max_quantity,
  ]
    .filter((value) => value != null && value !== '')
    .map(Number)
    .filter((value) => Number.isFinite(value) && value >= 0)
  if (selectedAttribute?.stock?.is_sold_out) limits.push(0)
  return Math.min(cartQuantity + 1, ...limits)
}

const { toggleCartNotice } = modalDuck.creators
const { fetch: fetchCart } = cartDuck.creators

const isConfirmedCart = (cart) =>
  cart &&
  cart.ok !== false &&
  !cart.error &&
  Array.isArray(cart.items) &&
  (cart.items.length === 0 ||
    (cart.summary?.total != null &&
      cart.summary.total !== '' &&
      Number.isFinite(Number(cart.summary.total))))

export const hasCartQuantityIncrease = (before, after, productId, sizeId) => {
  const beforeQuantity = getCartQuantity(before, productId, sizeId)
  const afterQuantity = getCartQuantity(after, productId, sizeId)
  return (
    beforeQuantity != null &&
    afterQuantity != null &&
    afterQuantity > beforeQuantity
  )
}

function useAddToBag({
  enableInterstitial = true,
  enableGATracking = true,
  enableSegment = true,
  onError,
  onFinish = () => null,
  onSuccess = () => null,
  product = null,
  refData = null,
  sizeData = null,
  applyStoreCredit,
  isQuickAddToBag = true,
  requireQuantityIncrease = false,
  validateCartResult,
}) {
  return async (dispatch, getState) => {
    if (!product || !sizeData) return null

    const { cart, cartData } = getState()
    const body = {
      is_store_credits_applied: applyStoreCredit ?? cartData.isStoreCreditUsed,
    }
    const reportFailure = (result) => {
      const message =
        result?.error?.message ||
        (typeof result?.error === 'string' && result.error) ||
        'Could not confirm the updated bag. Please refresh and try again.'
      if (enableInterstitial) {
        dispatch(toggleCartNotice(true, { error: message }))
      }
      onError?.(message)
      onFinish(result)
    }
    // Preserve the last authoritative voucher and avoid an unnecessary read.
    // An uncertain mutation must be reconciled before calculating another add.
    const initialCart =
      isConfirmedCart(cart.payload) &&
      !cart.didInvalidate &&
      !cart.needsReconciliation
        ? cart.payload
        : await dispatch(
            fetchCart({ type: 'getCart', keepPayload: true, body }),
          )

    if (initialCart?.stale) return initialCart

    // Both quick-add pilots validate before a write and before success tracking.
    if (validateCartResult && !validateCartResult(initialCart, 'prepare'))
      return initialCart

    if (!isConfirmedCart(initialCart)) {
      reportFailure(initialCart)
      return initialCart
    }

    // A failed read can be retried safely after a confirmed refresh. An
    // uncertain write (including one followed by failed reads) needs review
    // before a new add, so the same purchase intent is never replayed.
    if (
      cart.needsReconciliation &&
      (cart.operation?.kind !== 'view' || cart.operation?.requiresReview)
    ) {
      const th = getState().internationalization?.language?.startsWith('th')
      const reconciled = {
        ok: false,
        cart: initialCart,
        error: {
          message: th
            ? 'รีเฟรชถุงช้อปปิ้งแล้ว กรุณาตรวจสอบรายการก่อนเพิ่มสินค้าอีกครั้ง'
            : 'Your bag has been refreshed. Please review its items before adding again.',
        },
      }
      reportFailure(reconciled)
      return reconciled
    }

    const quantityCart = getAddedProdSizeQtyInCart(
      initialCart,
      product,
      sizeData.id_product_attribute,
    )
    const currentQuantity = getCartQuantity(
      initialCart,
      product.id_product,
      sizeData.id_product_attribute,
    )
    if (
      currentQuantity == null ||
      !Number.isInteger(quantityCart) ||
      quantityCart <= currentQuantity
    ) {
      const th = getState().internationalization?.language?.startsWith('th')
      const unavailable = {
        ok: false,
        error: {
          code: ADD_TO_BAG_API_CODE.overLimit,
          message: th
            ? 'ไม่สามารถเพิ่มจำนวนสินค้านี้ได้ กรุณาตรวจสอบจำนวนในถุงช้อปปิ้ง'
            : 'We couldn’t add another of this item. Please check the quantity in your bag.',
        },
      }
      reportFailure(unavailable)
      return unavailable
    }

    const res = await dispatch(
      fetchCart({
        productAttributeID: sizeData.id_product_attribute,
        productID: product.id_product,
        quantity: quantityCart,
        segmentAnonymousID: StateStorage.getSegmentAnonymousId(),
        type: 'AddToCart',
        skipCartRender: true, // Temp
        keepPayload: true, // Prevent payload clearing on error
        body,
      }),
    )

    if (res?.stale) return res
    if (validateCartResult && !validateCartResult(res, 'add')) return res

    if (!isConfirmedCart(res)) {
      reportFailure(res)
      return res
    }

    if (
      requireQuantityIncrease &&
      !hasCartQuantityIncrease(
        initialCart,
        res,
        product.id_product,
        sizeData.id_product_attribute,
      )
    ) {
      const unconfirmed = {
        ok: false,
        cart: res,
        error: { message: 'Could not confirm an increased cart quantity' },
      }
      reportFailure(unconfirmed)
      return unconfirmed
    }
    // TECH-74: a confirmed add tells us a size the shopper actually buys, so
    // search can surface items in her size (per-browser; the cross-device
    // profile is backend-owned). Best-effort - never blocks the add.
    rememberPreferredSize(sizeData?.size)

    if (enableInterstitial) {
      dispatch(toggleCartNotice(true, { product }))
    }
    const {
      // Price structure from lookbook content
      discounted_price,
      formatted_discounted_price: discounted_price_formatted,
      formatted_price: original_price_formatted,
      price: original_price,
      prices,
    } = product
    // Price structure on product details (and the rest?)
    let tempPrice = prices

    // One size recommendations pass no prices; they are filled in from the
    // returned cart below.
    if (!tempPrice) {
      tempPrice = {
        discounted_price,
        discounted_price_formatted,
        original_price,
        original_price_formatted,
      }
    }

    // Callers such as the cart's one-size recommendations pass a bare product
    // with no price, so the AddToCart event would reach Meta without a value.
    // The cart the add returned always has the item's price; use it instead.
    if (
      tempPrice.discounted_price == null &&
      tempPrice.original_price == null
    ) {
      // The same product can be in the bag in several sizes: prefer the line
      // of the size just added.
      const sameProduct = (res?.items || []).filter(
        (item) => String(item?.id_product) === String(product.id_product),
      )
      const cartItemPrices = (
        sameProduct.find((item) =>
          item?.attributes?.some(
            ({ selected, id_product_attribute: id }) =>
              selected && String(id) === String(sizeData?.id_product_attribute),
          ),
        ) || sameProduct[0]
      )?.prices

      if (cartItemPrices) {
        tempPrice = cartItemPrices
      }
    }

    if (enableGATracking && tempPrice) {
      ExchangeRateApiHandler.convertTo(
        tempPrice.discounted_price || tempPrice.original_price || 0,
      ).then((priceUsd) => {
        Tracking.trackEvent(Tracking.EVENT_NAME_ADD_TO_CART, {
          'add-to-cart-id': product.id_product || product.id_product_2,
          'add-to-cart-price-usd': priceUsd,
        })
      })
    }

    if (enableSegment) {
      const idCart = res.metadata?.id_cart
      const segmentPrice =
        tempPrice.discounted_price ?? tempPrice.original_price

      if (isQuickAddToBag) {
        segmentQuickAddToBagClicked({
          id_product: product?.id_product,
          price: segmentPrice,
          size: sizeData?.size,
          product_name: product?.name,
          id_site_category: product?.category?.id_category,
          ...refData,
        })
      } else {
        segmentTrackProductAdded(
          { ...product, prices: tempPrice },
          sizeData,
          refData,
          idCart,
        )
      }
    }

    if (onSuccess) {
      onSuccess(res, { product, sizeData })
      await updateLagacyShoppingTrack(res)
    }
    if (onFinish) {
      onFinish(res)
    }

    return res
  }
}

export default useAddToBag
